import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Star } from "lucide-react";
import api from "../lib/api";
import { Reveal } from "../components/motion";

const PASTELS = ["#FBE7EF", "#E4F6E9", "#FFF3D1", "#EEE8FF", "#FBE7EF", "#DFF3FF"];

export default function MeetBuddies() {
  const [buddies, setBuddies] = useState([]);

  useEffect(() => {
    api.get("/buddies").then((r) => setBuddies(r.data)).catch(() => {});
  }, []);

  const launched = buddies.filter((b) => b.status === "launched");
  const future = buddies.filter((b) => b.status === "coming_soon");

  return (
    <div data-testid="meet-buddies-page" className="bg-cream">
      <section className="px-6 py-20 text-center lg:py-28">
        <Reveal>
          <p className="text-xs font-bold uppercase tracking-[0.3em] text-pinkdeep">The character universe</p>
          <h1 className="mx-auto mt-5 max-w-3xl font-display text-4xl font-medium leading-tight text-navy sm:text-5xl">
            Who will become their new best friend?
          </h1>
          <p className="mx-auto mt-5 max-w-xl text-base text-navy/55">
            Every Buddy has a name, a personality, a voice and their own stories. See who your little dreamer clicks with.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl space-y-14 px-6 pb-24">
        {launched.map((b, i) => (
          <Reveal key={b.slug}>
            <div className={`grid items-center gap-10 rounded-[2.5rem] p-8 sm:p-12 lg:grid-cols-2 ${i % 2 ? "lg:[direction:rtl]" : ""}`}
              style={{ background: PASTELS[i % PASTELS.length] }}
              data-testid={`buddy-profile-${b.slug}`}>
              <div className="[direction:ltr]">
                <div className="overflow-hidden rounded-[2rem] bg-white/60 shadow-[0_16px_50px_rgba(16,42,86,0.08)]">
                  <img src={b.image} alt={`${b.name} the ${b.animal}`} loading="lazy" className="aspect-[2/3] w-full object-cover" />
                </div>
              </div>
              <div className="[direction:ltr]">
                <p className="text-xs font-bold uppercase tracking-[0.3em] text-pinkdeep">{b.personality}</p>
                <h2 className="mt-3 font-display text-3xl font-medium text-navy sm:text-4xl">{b.name} the {b.animal}</h2>
                <p className="mt-4 font-display text-lg italic text-navy/70">“{b.quote}”</p>
                <p className="mt-4 max-w-md text-base leading-relaxed text-navy/60">{b.long_desc || b.short_desc}</p>
                <div className="mt-6 flex flex-wrap gap-2">
                  {(b.favorite_things || []).map((t) => (
                    <span key={t} className="flex items-center gap-1.5 rounded-full bg-white/80 px-3.5 py-1.5 text-xs font-semibold text-navy/65">
                      <Star size={11} className="text-pink" /> {t}
                    </span>
                  ))}
                </div>
                <div className="mt-6 flex items-center gap-4">
                  <span className="font-display text-2xl font-semibold text-navy">${b.price.toFixed(2)}</span>
                </div>
                <Link to={`/buddy/${b.slug}`} data-testid={`profile-meet-${b.slug}`}
                  className="mt-7 inline-flex items-center gap-2 rounded-full bg-pink px-7 py-3.5 font-display font-semibold tracking-wide text-navy transition hover:scale-[1.04] hover:bg-moon">
                  MEET {b.name.toUpperCase()} <ArrowRight size={17} />
                </Link>
              </div>
            </div>
          </Reveal>
        ))}
      </section>

      {future.length > 0 && (
        <section className="bg-white px-6 py-20" data-testid="future-buddies-section">
          <div className="mx-auto max-w-7xl">
            <Reveal>
              <h2 className="font-display text-3xl font-medium text-navy">New friends are on their way.</h2>
              <p className="mt-3 text-navy/55">Join the family to hear the moment they land.</p>
            </Reveal>
            <div className="mt-10 flex gap-5 overflow-x-auto pb-4 no-scrollbar sm:grid sm:grid-cols-3 lg:grid-cols-6 lg:overflow-visible">
              {future.map((b) => (
                <div key={b.slug} className="w-40 shrink-0 overflow-hidden rounded-[1.5rem] bg-cream sm:w-auto" data-testid={`future-buddy-${b.slug}`}>
                  <img src={b.image} alt={`${b.name} the ${b.animal}, coming soon`} loading="lazy" className="aspect-square w-full object-cover opacity-90" />
                  <div className="p-4">
                    <p className="font-display font-semibold text-navy">{b.name}</p>
                    <p className="text-xs font-semibold text-pinkdeep">Coming soon</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
